/**
 * MembersSection — membrii spațiului curent: roluri, eliminare, părăsire.
 *
 * Regulile sunt aplicate de server (api/members.py); aici doar ascundem ce
 * oricum ar fi refuzat: owner gestionează pe oricine, admin doar member și
 * viewer, iar spațiul nu poate rămâne fără owner.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/api/client';
import { tenantsApi, type TenantMember } from '@/api/tenants';
import { formatDate } from '@/lib/format';
import { useAuthStore } from '@/stores/auth';

type Role = TenantMember['role'];
const ROLES: Role[] = ['owner', 'admin', 'member', 'viewer'];

interface Props {
  tenantId: string;
  myRole: Role;
}

export default function MembersSection({ tenantId, myRole }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const myId = useAuthStore((s) => s.user?.id);
  const removeTenant = useAuthStore((s) => s.removeTenant);

  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const members = useQuery({
    queryKey: ['tenant-members', tenantId],
    queryFn: () => tenantsApi.listMembers(tenantId),
  });

  const fail = (err: unknown) =>
    setError(err instanceof ApiError ? err.detail : t('members.generic_error'));
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['tenant-members', tenantId] });

  const roleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: Role }) =>
      tenantsApi.changeMemberRole(tenantId, userId, role),
    onMutate: () => {
      setError(null);
      setNotice(null);
    },
    onSuccess: () => refresh(),
    onError: fail,
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => tenantsApi.removeMember(tenantId, userId),
    onMutate: () => {
      setError(null);
      setNotice(null);
    },
    onSuccess: (result, userId) => {
      setConfirmId(null);
      if (userId === myId) {
        // Am părăsit spațiul: datele lui nu mai au ce căuta în cache.
        queryClient.clear();
        removeTenant(tenantId);
        return;
      }
      void refresh();
      setNotice(
        result.resources_needing_rotation > 0
          ? t('members.removed_rotate', { count: result.resources_needing_rotation })
          : t('members.removed'),
      );
    },
    onError: fail,
  });

  const owners = members.data?.filter((m) => m.role === 'owner').length ?? 0;
  const canManage = (target: TenantMember) =>
    target.user_id !== myId &&
    (myRole === 'owner' ||
      (myRole === 'admin' && (target.role === 'member' || target.role === 'viewer')));
  const assignable = (role: Role) =>
    myRole === 'owner' || role === 'member' || role === 'viewer';
  const busy = roleMutation.isPending || removeMutation.isPending;

  return (
    <div data-testid="members-section">
      <p className="text-xs text-[var(--text-muted)] mb-3">{t('members.hint')}</p>

      {error && (
        <div
          className="text-sm text-red-600 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2 mb-3"
          role="alert"
        >
          {error}
        </div>
      )}
      {notice && (
        <div className="text-sm text-green-800 dark:text-green-300 bg-green-50 dark:bg-green-900/30 border border-green-300 dark:border-green-800 rounded px-3 py-2 mb-3">
          {notice}
        </div>
      )}

      {members.isLoading && (
        <p className="text-xs text-[var(--text-muted)]">{t('common.loading')}</p>
      )}

      <ul className="divide-y divide-[var(--border)]">
        {members.data?.map((m) => {
          const isMe = m.user_id === myId;
          const manageable = canManage(m);
          const lastOwner = m.role === 'owner' && owners <= 1;
          const confirming = confirmId === m.user_id;
          return (
            <li
              key={m.user_id}
              className="py-2.5 flex flex-wrap items-center gap-x-3 gap-y-2"
              data-testid="member-row"
            >
              <div className="flex-1 min-w-[12rem]">
                <div className="text-sm break-all">
                  {m.email}
                  {isMe && (
                    <span className="ml-2 text-[10px] uppercase text-[var(--text-dim)]">
                      {t('members.you')}
                    </span>
                  )}
                </div>
                <div className="text-xs text-[var(--text-dim)]">
                  {t('members.joined', {
                    when: formatDate(m.joined_at),
                  })}
                </div>
              </div>

              {manageable ? (
                <select
                  className="input w-28 text-xs"
                  aria-label={t('members.role_of', { email: m.email })}
                  value={m.role}
                  disabled={busy || lastOwner}
                  onChange={(e) =>
                    roleMutation.mutate({ userId: m.user_id, role: e.target.value as Role })
                  }
                >
                  {ROLES.filter((r) => r === m.role || assignable(r)).map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="text-xs font-mono text-[var(--text-muted)] w-28" data-testid="member-role">
                  {m.role}
                </span>
              )}

              <div className="w-44 flex justify-end gap-2">
                {confirming ? (
                  <>
                    <button
                      type="button"
                      className="btn-outline text-xs text-red-600 dark:text-red-400"
                      disabled={busy}
                      onClick={() => removeMutation.mutate(m.user_id)}
                    >
                      {isMe ? t('members.leave_confirm') : t('members.remove_confirm')}
                    </button>
                    <button
                      type="button"
                      className="btn-outline text-xs"
                      disabled={busy}
                      onClick={() => setConfirmId(null)}
                    >
                      {t('common.cancel')}
                    </button>
                  </>
                ) : isMe ? (
                  <button
                    type="button"
                    className="btn-outline text-xs"
                    disabled={busy || lastOwner}
                    title={lastOwner ? t('members.last_owner') : undefined}
                    data-testid="member-leave"
                    onClick={() => setConfirmId(m.user_id)}
                  >
                    {t('members.leave')}
                  </button>
                ) : manageable ? (
                  <button
                    type="button"
                    className="btn-outline text-xs"
                    disabled={busy}
                    onClick={() => setConfirmId(m.user_id)}
                  >
                    {t('members.remove')}
                  </button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
